import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BookOpen, Printer, Loader2, List, ThumbsUp, ThumbsDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const OVERALL = 'overall';

// Votes are shared via context so casting a vote only re-renders the small
// thumbs control — NOT the whole parsed markdown tree.
const VotesContext = createContext({ votes: {}, onVote: () => {} });

// Slugify heading text → stable anchor id (shared by TOC + heading renderers)
const slugify = (text) =>
  String(text)
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');

// Pull plain text out of react-markdown children (handles nested nodes)
const childrenToText = (children) =>
  React.Children.toArray(children)
    .map((c) => (typeof c === 'string' ? c : c?.props?.children ? childrenToText(c.props.children) : ''))
    .join('');

// Compact inline thumbs shown to the right of each section heading.
function SectionThumbs({ section }) {
  const { votes, onVote } = useContext(VotesContext);
  const my = votes[section]?.my_vote;
  return (
    <span className="flex items-center gap-1 shrink-0" data-testid={`section-feedback-${section}`}>
      <span className="text-[10px] font-normal text-slate-400 mr-1 hidden sm:inline">Helpful?</span>
      <button
        type="button"
        onClick={() => onVote(section, true)}
        aria-pressed={my === true}
        title="Helpful"
        className={`p-1 rounded transition-colors ${
          my === true ? 'text-emerald-600 bg-emerald-50' : 'text-slate-300 hover:text-emerald-600'
        }`}
        data-testid={`section-feedback-${section}-yes`}
      >
        <ThumbsUp className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => onVote(section, false)}
        aria-pressed={my === false}
        title="Not helpful"
        className={`p-1 rounded transition-colors ${
          my === false ? 'text-rose-600 bg-rose-50' : 'text-slate-300 hover:text-rose-600'
        }`}
        data-testid={`section-feedback-${section}-no`}
      >
        <ThumbsDown className="w-3.5 h-3.5" />
      </button>
    </span>
  );
}

// Overall "Was this guide helpful?" widget at the foot of the guide.
function OverallFeedback({ data, onVote }) {
  const total = (data?.yes || 0) + (data?.no || 0);
  return (
    <div
      className="mt-12 border-t border-slate-200 pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
      data-testid="guide-feedback"
    >
      <div>
        <p className="text-sm font-semibold text-[#0F2040]">Was this guide helpful overall?</p>
        {total > 0 && (
          <p className="text-xs text-slate-400 mt-0.5" data-testid="guide-feedback-tally">
            {data.yes} of {total} found this helpful
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onVote(OVERALL, true)}
          aria-pressed={data?.my_vote === true}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
            data?.my_vote === true
              ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
              : 'border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
          data-testid="guide-feedback-yes"
        >
          <ThumbsUp className="w-3.5 h-3.5" /> Yes
        </button>
        <button
          type="button"
          onClick={() => onVote(OVERALL, false)}
          aria-pressed={data?.my_vote === false}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
            data?.my_vote === false
              ? 'bg-rose-50 border-rose-300 text-rose-700'
              : 'border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
          data-testid="guide-feedback-no"
        >
          <ThumbsDown className="w-3.5 h-3.5" /> No
        </button>
      </div>
    </div>
  );
}

// Optional "what was missing?" box — appears under a heading after a 👎 vote.
function SectionCommentBox({ section }) {
  const { votes, onVote } = useContext(VotesContext);
  const info = votes[section];
  const myComment = info?.my_comment || '';
  const [text, setText] = useState(myComment);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setText(myComment);
  }, [myComment]);

  if (info?.my_vote !== false) return null;

  const submit = async () => {
    setSaving(true);
    try {
      await onVote(section, false, text.trim());
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="-mt-1 mb-5 flex items-center gap-2" data-testid={`section-comment-${section}`}>
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="What was missing or unclear? (optional)"
        maxLength={500}
        className="flex-1 text-xs border border-slate-200 rounded-md px-2.5 py-1.5 text-slate-700 focus:outline-none focus:border-[#4ABFCF]"
        data-testid={`section-comment-input-${section}`}
      />
      <button
        type="button"
        onClick={submit}
        disabled={saving}
        className="shrink-0 text-xs font-medium px-2.5 py-1.5 rounded-md border border-[#4ABFCF] text-[#0F2040] hover:bg-[#4ABFCF]/10 transition-colors disabled:opacity-60"
        data-testid={`section-comment-send-${section}`}
      >
        Send
      </button>
    </div>
  );
}

// Stable react-markdown component map. The H2 renderer adds per-section
// thumbs (which read vote state from context, so this map never changes).
const mdComponents = {
  h1: ({ children }) => (
    <h1 className="text-3xl font-bold text-[#0F2040] tracking-tight mb-2 mt-0" data-testid="guide-h1">
      {children}
    </h1>
  ),
  h2: ({ children }) => {
    const id = slugify(childrenToText(children));
    return (
      <>
        <h2
          id={id}
          className="text-xl font-bold text-[#0F2040] mt-12 mb-3 pb-2 border-b border-slate-200 scroll-mt-24 flex items-center justify-between gap-3"
          data-testid={`guide-section-${id}`}
        >
          <span className="min-w-0">{children}</span>
          <SectionThumbs section={id} />
        </h2>
        <SectionCommentBox section={id} />
      </>
    );
  },
  h3: ({ children }) => {
    const id = slugify(childrenToText(children));
    return (
      <h3 id={id} className="text-base font-semibold text-[#0F2040] mt-6 mb-2 scroll-mt-24">
        {children}
      </h3>
    );
  },
  h4: ({ children }) => (
    <h4 className="text-sm font-semibold text-slate-700 mt-4 mb-1.5">{children}</h4>
  ),
  p: ({ children }) => <p className="text-sm text-slate-600 leading-relaxed my-3">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-6 my-3 space-y-1 text-sm text-slate-600">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-6 my-3 space-y-1 text-sm text-slate-600">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  a: ({ children, href }) => (
    <a href={href} className="text-[#4ABFCF] font-medium hover:underline" target="_blank" rel="noreferrer">
      {children}
    </a>
  ),
  strong: ({ children }) => <strong className="font-semibold text-[#0F2040]">{children}</strong>,
  em: ({ children }) => <em className="italic text-slate-500">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-[#4ABFCF] bg-[#4ABFCF]/5 rounded-r-md pl-4 pr-3 py-2 my-4 text-sm text-slate-600 [&_p]:my-1.5">
      {children}
    </blockquote>
  ),
  code: ({ inline, children }) =>
    inline ? (
      <code className="bg-slate-100 text-[#0F2040] rounded px-1.5 py-0.5 text-[12px] font-mono">{children}</code>
    ) : (
      <code className="block bg-[#0F2040] text-slate-100 rounded-lg p-4 my-4 text-[12px] font-mono overflow-x-auto whitespace-pre-wrap leading-relaxed">
        {children}
      </code>
    ),
  hr: () => <hr className="my-8 border-slate-200" />,
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-lg border border-slate-200">
      <table className="w-full text-sm border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-50">{children}</thead>,
  th: ({ children }) => (
    <th className="text-left px-3 py-2 font-semibold text-slate-600 border-b border-slate-200">{children}</th>
  ),
  td: ({ children }) => <td className="px-3 py-2 text-slate-600 border-b border-slate-100 align-top">{children}</td>,
};

export default function UserGuidePage() {
  const location = useLocation();
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [votes, setVotes] = useState({}); // { [section]: { yes, no, my_vote } }

  useEffect(() => {
    let mounted = true;
    fetch(`${process.env.PUBLIC_URL || ''}/user-guide.md`, { cache: 'no-cache' })
      .then((r) => {
        if (!r.ok) throw new Error('not ok');
        return r.text();
      })
      .then((text) => {
        if (mounted) {
          setMarkdown(text);
          setLoading(false);
        }
      })
      .catch(() => {
        if (mounted) {
          setError(true);
          setLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Load all feedback tallies (per section + overall) in one call.
  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API_URL}/api/guide/feedback`, { withCredentials: true })
      .then((r) => mounted && setVotes(r.data?.sections || {}))
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const voteSection = useCallback(async (section, helpful, comment) => {
    try {
      const body = { helpful, section };
      if (comment !== undefined) body.comment = comment;
      const r = await axios.post(`${API_URL}/api/guide/feedback`, body, { withCredentials: true });
      setVotes((prev) => ({
        ...prev,
        [section]: { yes: r.data.yes, no: r.data.no, my_vote: r.data.my_vote, my_comment: r.data.my_comment },
      }));
      if (comment !== undefined) {
        toast.success('Thanks — noted for the next guide update.');
      } else {
        toast.success(helpful ? 'Thanks for the feedback!' : 'Thanks — we’ll keep improving it.');
      }
    } catch {
      toast.error('Could not record your feedback. Please try again.');
    }
  }, []);

  const ctxValue = useMemo(() => ({ votes, onVote: voteSection }), [votes, voteSection]);

  // Memoize the parsed markdown so votes changing never re-parses it; the
  // thumbs update independently via context.
  const renderedMarkdown = useMemo(
    () => (
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
        {markdown}
      </ReactMarkdown>
    ),
    [markdown],
  );

  // Build the table of contents from the H2 headings (## ...)
  const toc = useMemo(() => {
    const lines = markdown.split('\n');
    const out = [];
    let inFence = false;
    for (const line of lines) {
      if (line.trim().startsWith('```')) inFence = !inFence;
      if (inFence) continue;
      const m = line.match(/^##\s+(.+)$/);
      if (m) {
        const title = m[1].replace(/[*_`]/g, '').trim();
        out.push({ id: slugify(title), title });
      }
    }
    return out;
  }, [markdown]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Deep-link support: when arriving with a #section hash (from a page's
  // contextual "?" help button), scroll to that section once content renders.
  // Polls briefly because the markdown renders asynchronously after fetch.
  useEffect(() => {
    if (loading || error || !markdown) return;
    const hash = decodeURIComponent((location.hash || '').replace('#', ''));
    if (!hash) return;
    let tries = 0;
    let timer;
    const tryScroll = () => {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (tries++ < 25) timer = setTimeout(tryScroll, 100);
    };
    timer = setTimeout(tryScroll, 100);
    return () => clearTimeout(timer);
  }, [loading, error, markdown, location.hash]);

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6 print:px-8" data-testid="user-guide-page">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-xl font-bold text-[#0F2040] flex items-center gap-2" data-testid="user-guide-title">
            <BookOpen className="w-5 h-5 text-[#4ABFCF]" />
            User Guide
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Everything you need to navigate Blueprint Studio — from your first estimate to a customer-ready Design Pack.
          </p>
        </div>
        <button
          onClick={() => window.print()}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
          data-testid="user-guide-print-btn"
        >
          <Printer className="w-3.5 h-3.5" /> Print
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-24 text-slate-400" data-testid="user-guide-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading guide…
        </div>
      )}

      {error && (
        <div className="py-16 text-center text-slate-500" data-testid="user-guide-error">
          Couldn't load the user guide right now. Please refresh the page.
        </div>
      )}

      {!loading && !error && (
        <div className="flex gap-8">
          {/* Sticky Table of Contents */}
          <aside className="hidden lg:block w-60 shrink-0 print:hidden" data-testid="user-guide-toc">
            <div className="sticky top-24">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-3">
                <List className="w-3.5 h-3.5" /> On this page
              </div>
              <nav className="space-y-0.5 border-l border-slate-200">
                {toc.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollTo(item.id)}
                    className="block w-full text-left text-xs text-slate-500 hover:text-[#0F2040] hover:border-[#4ABFCF] border-l-2 border-transparent -ml-px pl-3 py-1.5 transition-colors"
                    data-testid={`user-guide-toc-${item.id}`}
                  >
                    {item.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <article
            className="min-w-0 flex-1 max-w-3xl bg-white rounded-xl border border-slate-200 px-6 sm:px-10 py-8 print:border-0 print:px-0 print:py-0"
            data-testid="user-guide-content"
          >
            <VotesContext.Provider value={ctxValue}>{renderedMarkdown}</VotesContext.Provider>
            <OverallFeedback data={votes[OVERALL]} onVote={voteSection} />
          </article>
        </div>
      )}
    </div>
  );
}
